import { Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getKYCStatus } from "@/lib/appwrite/api";
import { getUserAverageRating } from "@/lib/appwrite/trustScore";

type SellerRatingBadgeProps = {
  creatorId?: string;
};

type SellerRating = {
  average: number;
  count: number;
};

async function loadSellerRating(creatorId: string): Promise<SellerRating | null> {
  const kyc = await getKYCStatus(creatorId);
  if (!kyc.panVerified || !kyc.bankVerified) return null;

  const rating = await getUserAverageRating(creatorId);
  if (!rating || rating.count <= 0) return null;

  return {
    average: Number(rating.average),
    count: rating.count,
  };
}

export default function SellerRatingBadge({ creatorId }: SellerRatingBadgeProps) {
  const { data: rating } = useQuery({
    queryKey: ["seller-rating", creatorId],
    queryFn: () => loadSellerRating(creatorId!),
    enabled: Boolean(creatorId),
    staleTime: 60_000,
    retry: 1,
  });

  if (!rating || !Number.isFinite(rating.average)) return null;

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800 whitespace-nowrap"
      title="Average rating from completed deals"
      aria-label={`Seller rating ${rating.average.toFixed(2)} out of 5 from ${rating.count} ${rating.count === 1 ? "rating" : "ratings"}`}
    >
      <Star size={13} fill="currentColor" aria-hidden="true" />
      {rating.average.toFixed(2)}
      <span className="font-normal text-amber-700">({rating.count})</span>
    </span>
  );
}
